/**
 * Typed path helpers for the Grape API (/api/v1).
 *
 * Rails path helpers (see ./routes.js) only cover Rails-native routes.
 * Grape is mounted as a Rack engine, so js-routes can't introspect it —
 * this file is the hand-maintained mirror of config/routes.rb for the
 * Grape mount. Keep it in sync when you add / rename Grape endpoints.
 *
 * Conventions:
 *   - Function names mirror the Rails `_path` helper style (snake_case)
 *   - Positional args match the path segments (no `{}` wrapper object)
 *   - Dynamic segments are URL-encoded; callers never need to encode
 *   - No query strings here; callers append `?foo=bar` themselves
 *   - Return type is always `string` (not `URL`) to match fetch() ergonomics
 *
 * Usage:
 *   import { api_v1_project_path, api_v1_projects_path } from '../shared/api-routes'
 *   fetch(api_v1_projects_path())
 *   fetch(api_v1_project_path(42))
 */

const BASE = "/api/v1"

function enc(value: string | number): string {
  return encodeURIComponent(String(value))
}

// ── Auth ────────────────────────────────────────────────────
export const api_v1_auth_login_path        = (): string => `${BASE}/auth/login`
export const api_v1_auth_register_path     = (): string => `${BASE}/auth/register`
export const api_v1_auth_refresh_path      = (): string => `${BASE}/auth/refresh`
export const api_v1_auth_logout_path       = (): string => `${BASE}/auth/logout`
export const api_v1_auth_oauth_google_path   = (): string => `${BASE}/auth/oauth/google`
export const api_v1_auth_oauth_facebook_path = (): string => `${BASE}/auth/oauth/facebook`
export const api_v1_auth_oauth_apple_path    = (): string => `${BASE}/auth/oauth/apple`

// ── Users ───────────────────────────────────────────────────
export const api_v1_users_me_path              = (): string => `${BASE}/users/me`
export const api_v1_users_me_preferences_path  = (): string => `${BASE}/users/me/preferences`

// ── Projects ────────────────────────────────────────────────
export const api_v1_projects_path          = (): string => `${BASE}/projects`
export const api_v1_project_path           = (id: string | number): string => `${BASE}/projects/${enc(id)}`
export const api_v1_project_pdf_path       = (id: string | number): string => `${BASE}/projects/${enc(id)}/pdf`
export const api_v1_project_pdf_status_path = (id: string | number): string => `${BASE}/projects/${enc(id)}/pdf/status`
export const api_v1_projects_from_sketch_path = (): string => `${BASE}/projects/from-sketch`

// ── Brands & products ──────────────────────────────────────
export const api_v1_brands_path            = (): string => `${BASE}/brands`
export const api_v1_brand_path             = (slug: string): string => `${BASE}/brands/${enc(slug)}`
export const api_v1_nozzles_path           = (): string => `${BASE}/nozzles`
export const api_v1_nozzle_path            = (model: string): string => `${BASE}/nozzles/${enc(model)}`

// ── Catalog (equipment) ────────────────────────────────────
export const api_v1_valves_path                 = (): string => `${BASE}/valves`
export const api_v1_spray_heads_path            = (): string => `${BASE}/spray_heads`
export const api_v1_series_path                 = (): string => `${BASE}/series`
export const api_v1_fittings_path               = (): string => `${BASE}/fittings`
export const api_v1_filters_path                = (): string => `${BASE}/filters`
export const api_v1_pipes_path                  = (): string => `${BASE}/pipes`
export const api_v1_valve_boxes_path            = (): string => `${BASE}/valve_boxes`
export const api_v1_weather_stations_path       = (): string => `${BASE}/weather_stations`
export const api_v1_installation_equipment_path = (): string => `${BASE}/installation_equipment`
export const api_v1_drip_types_path             = (): string => `${BASE}/drip_types`
// Grape mounts irrigation_controllers at /controllers (see app/api/v1/base.rb);
// keep the helper name aligned with the actual URL rather than the model name.
export const api_v1_controllers_path            = (): string => `${BASE}/controllers`

// ── Marketplace ─────────────────────────────────────────────
export const api_v1_marketplace_path              = (): string => `${BASE}/marketplace`
export const api_v1_marketplace_my_templates_path = (): string => `${BASE}/marketplace/my-templates`
export const api_v1_marketplace_my_purchases_path = (): string => `${BASE}/marketplace/my-purchases`
export const api_v1_marketplace_item_path         = (id: string | number): string => `${BASE}/marketplace/${enc(id)}`
export const api_v1_marketplace_purchase_path  = (id: string | number): string => `${BASE}/marketplace/${enc(id)}/purchase`
export const api_v1_marketplace_rate_path      = (id: string | number): string => `${BASE}/marketplace/${enc(id)}/rate`
export const api_v1_marketplace_comments_path  = (id: string | number): string => `${BASE}/marketplace/${enc(id)}/comments`
export const api_v1_marketplace_comment_path   = (id: string | number, commentId: string | number): string =>
  `${BASE}/marketplace/${enc(id)}/comments/${enc(commentId)}`

// ── Article comments ───────────────────────────────────────
export const api_v1_article_comments_path = (articleId: string | number): string => `${BASE}/articles/${enc(articleId)}/comments`
export const api_v1_article_comment_path  = (articleId: string | number, commentId: string | number): string =>
  `${BASE}/articles/${enc(articleId)}/comments/${enc(commentId)}`

// ── AI ──────────────────────────────────────────────────────
export const api_v1_ai_analyze_project_path = (): string => `${BASE}/ai/analyze-project`
export const api_v1_ai_chat_path            = (): string => `${BASE}/ai/chat`
export const api_v1_ai_nlp_command_path     = (): string => `${BASE}/ai/nlp-command`
export const api_v1_ai_optimize_bom_path    = (): string => `${BASE}/ai/optimize-bom`
export const api_v1_ai_smart_place_path     = (): string => `${BASE}/ai/smart-place`

// ── Misc ────────────────────────────────────────────────────
export const api_v1_config_path         = (): string => `${BASE}/config`
export const api_v1_features_path       = (): string => `${BASE}/features`
export const api_v1_locale_path         = (lang: string): string => `${BASE}/locale/${enc(lang)}`
export const api_v1_faqs_path           = (): string => `${BASE}/faqs`
export const api_v1_search_path         = (): string => `${BASE}/search`
export const api_v1_feedback_path       = (): string => `${BASE}/feedback`
export const api_v1_dashboard_path      = (): string => `${BASE}/dashboard`
export const api_v1_tiers_path          = (): string => `${BASE}/tiers`
export const api_v1_waitlist_path       = (): string => `${BASE}/waitlist`
export const api_v1_waitlist_count_path = (): string => `${BASE}/waitlist/count`
// The following are namespace prefixes — they have no bare GET. Callers
// must append a sub-path (e.g. `${api_v1_iot_base_path()}/controllers`).
// Kept exported for use when the child endpoints don't warrant their own helper.
export const api_v1_iot_base_path           = (): string => `${BASE}/iot`
export const api_v1_subscriptions_base_path = (): string => `${BASE}/subscriptions`
export const api_v1_templates_base_path     = (): string => `${BASE}/templates`
